import { getChatMessage } from '@/api/cs';
import { RefundModifyConfirmed, RefundModifyRejected } from '@/types/cs.type';
import { Modal } from '@successmode-ktp/kui';
import { format } from 'date-fns';
import { useState } from 'react';
import { useQuery } from 'react-query';
import styled, { css } from 'styled-components';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  refundId: number;
  requirementId: number;
};

function EditListModal({ isOpen, onClose, refundId, requirementId }: Props) {
  const [resultList, setResultList] = useState<
    RefundModifyRejected[] | RefundModifyConfirmed[]
  >([]);

  const { data } = useQuery(
    ['editedListPerRefund', requirementId],
    () => getChatMessage(requirementId),
    {
      onSuccess: (data) => {
        const newResultList = data.chat.messages.filter(
          (message) =>
            message.type !== 'REFUND_MODIFY_REQUESTED' &&
            message.type !== 'APPEND_REFUND_MODIFY_REQUESTED',
        );
        setResultList(
          newResultList as RefundModifyRejected[] | RefundModifyConfirmed[],
        );
      },
      enabled: isOpen,
    },
  );

  return (
    <Modal isOpen={isOpen} hasBackdrop>
      <Modal.Header>수정 내역</Modal.Header>
      <List>
        {resultList.map(
          ({ createdAt, requestReason, rejectionReason, type }) => (
            <Item key={refundId}>
              <ItemDate>
                요청 일시 : {format(new Date(createdAt), 'yyyy.MM.dd HH:mm')}
              </ItemDate>
              <Wrapper>
                <WrapperHeader>
                  <Type isCompleted={type === 'REFUND_MODIFY_CONFIRMED'}>
                    {type === 'REFUND_MODIFY_CONFIRMED'
                      ? '수정 완료'
                      : '수정 반려'}
                  </Type>
                  <Request>{requestReason}</Request>
                </WrapperHeader>
                {rejectionReason && <Response>{rejectionReason}</Response>}
              </Wrapper>
            </Item>
          ),
        )}
      </List>
      <Modal.Footer>
        <Modal.Button variant='cancel' onClick={onClose}>
          확인
        </Modal.Button>
      </Modal.Footer>
    </Modal>
  );
}

const List = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  width: 667px;
  margin-top: 16px;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
`;

const ItemDate = styled.p`
  color: #3a3b3e;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const Wrapper = styled.div`
  width: 100%;
  background-color: #f5f6f7;
  border: 1px solid #cbccce;
  border-radius: 12px;
  padding: 4px;
`;

const WrapperHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
`;

const Type = styled.span<{ isCompleted: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 85px;
  height: 32px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  border-radius: 8px;
  color: ${({ isCompleted }) => (isCompleted ? '#246CF6' : '#FF2D55')};
  background-color: ${({ isCompleted }) =>
    isCompleted ? '#F0F5FF' : '#FFF0F3'};
  border: 1px solid
    ${({ isCompleted }) =>
      isCompleted ? 'rgba(36, 108, 246, 0.5)' : 'rgba(255, 45, 85, 0.5)'};

  ${({ isCompleted }) =>
    isCompleted &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 6px;
        right: 12px;
        width: 3px;
        height: 3px;
        border-radius: 10px;
        background-color: #ff2d55;
      }
    `};
`;

const Request = styled.p`
  color: #3a3b3e;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
`;

const Response = styled.p`
  margin-top: 8px;
  padding: 10px 16px;
  min-height: 58px;
  color: #80848a;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  background-color: #ffffff;
  border-radius: 8px;
`;

export default EditListModal;
